import { useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { RedirectError } from "bunfx";
import { useEffect } from "preact/hooks";
import { IcoStar } from "../../components/icons";
import { BrandedLayout } from "../../components/ui";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import type { MimicState } from "../../stores/mimic";
import type {
  BrandColor,
  CoreValue,
  User,
  ValueRecognition,
} from "../../types";
import { isAdmin } from "../../types";

type DigestPageState = {
  currentUser: User;
  mimic: MimicState;
  orgId: string;
  orgName: string;
  brandColor: BrandColor;
  isOrgAdmin: boolean;
  hasReports: boolean;
  periods: { periodStart: Date; periodEnd: Date | null; count: number }[];
  recognitions: ValueRecognition[];
  values: CoreValue[];
  selectedPeriod: string | null;
};

export async function load(args: LoaderArgs): Promise<DigestPageState> {
  const orgId = args.params.orgId ?? "";

  // Stage 1: Auth
  const [authResult, orgsResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
  ]);

  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  const currentUser = authResult.user as User;
  const orgFromList = orgsResult.organizations.find((o) => o.id === orgId);
  const brandColor = (orgFromList?.brandColor ?? "indigo") as BrandColor;

  // Stage 2: Data
  const [orgResult, periodsResult, valuesResult] = await Promise.all([
    rpc.orgs.get({ id: orgId }),
    rpc.valueRecognitions.listPeriods({ workspaceId: orgId }),
    rpc.coreValues.list({ workspaceId: orgId }),
  ]);

  const isOrgAdmin = isAdmin(orgResult.currentMember.role);

  // Determine which period to show
  const urlPeriod = args.url.searchParams.get("period");
  const periods = periodsResult.periods;
  let selectedPeriod: string | null = null;
  let recognitions: ValueRecognition[] = [];

  if (periods.length > 0) {
    selectedPeriod =
      urlPeriod ?? new Date(periods[0]!.periodStart).toISOString();

    const recResult = await rpc.valueRecognitions.listByPeriod({
      workspaceId: orgId,
      periodStart: new Date(selectedPeriod),
    });
    recognitions = recResult.recognitions;
  }

  return {
    currentUser,
    mimic: authResult.mimic ?? null,
    orgId,
    orgName: orgResult.organization.name,
    brandColor,
    isOrgAdmin,
    hasReports: orgFromList?.hasReports ?? false,
    periods,
    recognitions,
    values: valuesResult.values,
    selectedPeriod,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Values Digest");
  const {
    currentUser,
    mimic,
    orgId,
    orgName,
    brandColor,
    isOrgAdmin,
    hasReports,
    values,
  } = state.value;

  const periods = useSignal(state.value.periods);
  const recognitions = useSignal(state.value.recognitions);
  const selectedPeriod = useSignal(state.value.selectedPeriod);
  const loading = useSignal(false);

  useEffect(() => {
    periods.value = state.value.periods;
    recognitions.value = state.value.recognitions;
    selectedPeriod.value = state.value.selectedPeriod;
  }, [
    state.value.periods,
    state.value.recognitions,
    state.value.selectedPeriod,
  ]);

  async function handlePeriodChange(periodIso: string) {
    selectedPeriod.value = periodIso;
    loading.value = true;
    try {
      const result = await rpc.valueRecognitions.listByPeriod({
        workspaceId: orgId,
        periodStart: new Date(periodIso),
      });
      recognitions.value = result.recognitions;
    } catch {
      // Keep current data on error
    } finally {
      loading.value = false;
    }
  }

  // Group recognitions by value
  const grouped = new Map<string, ValueRecognition[]>();
  for (const rec of recognitions.value) {
    for (const v of rec.values) {
      const list = grouped.get(v.name) ?? [];
      list.push(rec);
      grouped.set(v.name, list);
    }
  }

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={orgId}
      orgName={orgName}
      brandColor={brandColor}
      activeTab="values"
      isAdmin={isOrgAdmin}
      hasReports={hasReports}
    >
      <div class="flex flex-col gap-6">
        {/* Header */}
        <div class="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div class="flex items-center gap-2 mb-1">
              <a
                href={`/orgs/${orgId}/values`}
                class="text-sm text-text-muted hover:text-text transition-colors"
              >
                Core Values
              </a>
              <span class="text-text-muted">/</span>
              <span class="text-sm font-medium text-text">Digest</span>
            </div>
            <h2 class="text-lg font-semibold text-text">Values Digest</h2>
            <p class="text-sm text-text-muted mt-1">
              See how the team is living our values each week.
            </p>
          </div>

          {/* Period selector */}
          {periods.value.length > 0 && (
            <select
              value={selectedPeriod.value ?? ""}
              onChange={(e) => {
                const val = (e.target as HTMLSelectElement).value;
                if (val) handlePeriodChange(val);
              }}
              class="px-3 py-2 rounded-lg border border-border bg-surface text-text text-sm focus:outline-none focus:ring-2 focus:ring-text-subtle"
            >
              {periods.value.map((p) => {
                const start = new Date(p.periodStart);
                const fmtOpts: Intl.DateTimeFormatOptions = {
                  month: "short",
                  day: "numeric",
                };
                const startStr = start.toLocaleDateString("en-US", fmtOpts);
                const endStr = p.periodEnd
                  ? new Date(p.periodEnd).toLocaleDateString("en-US", fmtOpts)
                  : null;
                const label = endStr ? `${startStr} – ${endStr}` : startStr;
                return (
                  <option key={start.toISOString()} value={start.toISOString()}>
                    {label} ({p.count} recognition{p.count === 1 ? "" : "s"})
                  </option>
                );
              })}
            </select>
          )}
        </div>

        {/* Content */}
        {periods.value.length === 0 ? (
          <div class="bg-surface border border-border rounded-xl p-12 text-center">
            <div class="inline-flex items-center justify-center size-14 rounded-full bg-surface-muted mb-4">
              <IcoStar class="size-7 text-text-muted" />
            </div>
            <h3 class="text-base font-semibold text-text mb-2">
              No recognitions yet
            </h3>
            <p class="text-sm text-text-muted max-w-sm mx-auto">
              When team members recognize each other for living your values
              during check-ins, the digest will appear here.
            </p>
          </div>
        ) : recognitions.value.length === 0 ? (
          <div class="bg-surface border border-border rounded-xl p-8 text-center">
            <p class="text-sm text-text-muted">
              No recognitions for this period.
            </p>
          </div>
        ) : (
          <div
            class={`flex flex-col gap-8 transition-opacity ${loading.value ? "opacity-50" : ""}`}
          >
            {values
              .filter((v) => grouped.has(v.name))
              .map((v, i) => {
                const recs = grouped.get(v.name) ?? [];
                return (
                  <div key={v.id}>
                    {/* Value header */}
                    <div class="mb-4">
                      <h3 class="text-lg font-bold text-[var(--brand-600)]">
                        {i + 1}. {v.name}
                        {v.tagline && (
                          <span class="font-normal italic">
                            {" "}
                            — "{v.tagline}"
                          </span>
                        )}
                      </h3>
                    </div>

                    {/* Recognition entries */}
                    <div class="flex flex-col gap-3">
                      {recs.map((rec) => (
                        <div
                          key={rec.id}
                          class="bg-surface border border-border rounded-lg p-4"
                        >
                          <div class="text-sm text-text leading-relaxed">
                            <span class="font-semibold">{rec.author.name}</span>
                            <span class="text-text-muted"> – </span>
                            <span class="font-semibold">
                              {rec.recipients.map((r) => r.name).join(", ")}
                            </span>
                            {rec.message && (
                              <span class="text-text-muted">
                                {" "}
                                {rec.message.replace(/<[^>]*>/g, "")}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </BrandedLayout>
  );
}
